import { Button } from "@/modern/components/ui/button-shadcn";
import { FileText, Info, X } from "lucide-react";
import { format } from "date-fns";
import { useContext, useState, useEffect } from "react";
import { cn } from "@/modern/utils";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { useFileUrl } from "@/react/components/entityViews/files/api/files.api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { SpotIncentiveFormData } from "./spotIncentiveSchema";
import SpotIncentiveImagePreview, { ImageThumbnail } from "./SpotIncentiveImagePreview";
import { SPOT_INCENTIVE_DIALOG_Z } from "./SpotIncentivePickerShell";

interface SpotIncentivePreviewProps {
  data: SpotIncentiveFormData & { id?: number };
  editorInstance?: unknown;
  onClose?: () => void;
}

const FIELD_LABEL =
  "mb-1.5 block text-sm font-normal uppercase tracking-wide text-muted-foreground";

const SpotIncentivePreview = ({ data, onClose }: SpotIncentivePreviewProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { project } = useContext(BryntumContext) as any;
  const [isOpen, setIsOpen] = useState(true);
  const { url: fileUrl, thumbnailUrl: fileThumbnailUrl } = useFileUrl(data.fileId ?? null);

  useEffect(() => {
    setIsOpen(true);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  const getSelectedTask = (taskId: number | null) => {
    if (!taskId) return undefined;
    return project?.extraTaskStore?.getById(taskId);
  };

  const getSelectedResources = (resourceIds: number[]) => {
    return resourceIds.map((id) => project?.resourceStore?.getById(id)).filter(Boolean);
  };

  const getTagName = (tagId: number) => {
    const tag = project?.incentiveTagsStore?.getById(tagId);
    return tag?.name || `Tag ${tagId}`;
  };

  const getTotalPoints = () => {
    const positivePoints = data.positiveTagIds.reduce((sum: number, tagId: number) => {
      const tag = project?.incentiveTagsStore?.getById(tagId);
      return sum + (tag?.categoryRecord?.points || 0);
    }, 0);
    const negativePoints = data.negativeTagIds.reduce((sum: number, tagId: number) => {
      const tag = project?.incentiveTagsStore?.getById(tagId);
      return sum + (tag?.categoryRecord?.points || 0);
    }, 0);
    return positivePoints - negativePoints;
  };

  const hasPointsAssigned = data.positiveTagIds.length > 0 || data.negativeTagIds.length > 0;
  const totalPoints = getTotalPoints();

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        dialogOverlayClassName={SPOT_INCENTIVE_DIALOG_Z}
        className={cn(
          SPOT_INCENTIVE_DIALOG_Z,
          "flex max-h-[min(90vh,47rem)] w-[min(48rem,92vw)] max-w-[48rem] flex-col gap-0 overflow-hidden",
          "border-border/60 bg-background p-0 shadow-2xl sm:rounded-[2rem]",
        )}
      >
        <DialogTitle className="sr-only">{data.name || "Spot points"}</DialogTitle>
        <DialogDescription className="sr-only">Review this points adjustment</DialogDescription>

        <div className="flex shrink-0 items-start gap-3 px-5 pt-5 sm:px-7">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-xl font-normal tracking-tight text-foreground sm:text-2xl">
              {data.name || "Spot points"}
            </h2>
            <p className="mt-0.5 text-sm font-normal text-muted-foreground">Points adjustment</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleClose}
            aria-label="Close"
            className="size-9 shrink-0 rounded-full border-input bg-muted text-foreground/80 shadow-sm hover:border-input hover:text-foreground"
          >
            <X className="size-4" />
          </Button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 pt-5 sm:px-7">
          {/* L1 — Points */}
          {hasPointsAssigned ? (
            <div className="space-y-3 rounded-2xl border border-border/60 bg-muted/20 p-4">
              <div className="flex items-baseline justify-between gap-3">
                <p className={cn(FIELD_LABEL, "mb-0")}>Points</p>
                <p
                  className={cn(
                    "text-2xl font-normal tracking-tight tabular-nums",
                    totalPoints < 0 ? "text-destructive" : totalPoints > 0 ? "text-success" : "text-foreground",
                  )}
                >
                  {totalPoints > 0 ? "+" : ""}
                  {totalPoints}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.positiveTagIds.map((tagId) => {
                  const tag = project?.incentiveTagsStore?.getById(tagId);
                  const points = tag?.categoryRecord?.points || 0;
                  return (
                    <span
                      key={`pos-${tagId}`}
                      className="inline-flex h-9 items-center gap-1.5 rounded-full border border-success/30 bg-success/10 px-3 text-sm font-normal text-success"
                    >
                      {getTagName(tagId)}
                      <span className="tabular-nums">+{points}</span>
                    </span>
                  );
                })}
                {data.negativeTagIds.map((tagId) => {
                  const tag = project?.incentiveTagsStore?.getById(tagId);
                  const points = tag?.categoryRecord?.points || 0;
                  return (
                    <span
                      key={`neg-${tagId}`}
                      className="inline-flex h-9 items-center gap-1.5 rounded-full border border-destructive/30 bg-destructive/10 px-3 text-sm font-normal text-destructive"
                    >
                      {getTagName(tagId)}
                      <span className="tabular-nums">−{points}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="flex items-center gap-2 rounded-xl border border-border/50 bg-muted/30 px-3 py-2.5">
            <Info className="size-4 shrink-0 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Multiply by days on task is {data.multiplyByDays ? "on" : "off"}
            </span>
          </div>

          {data.taskId ? (
            <div className="space-y-1.5">
              <p className={FIELD_LABEL}>Task</p>
              <p className="text-sm font-normal text-foreground">
                {getSelectedTask(data.taskId)?.name}
              </p>
            </div>
          ) : null}

          {data.resourceIds.length > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className={cn(FIELD_LABEL, "mb-0")}>Resources ({data.resourceIds.length})</p>
                {data.startDate && data.endDate ? (
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(data.startDate), "MMM dd")} –{" "}
                    {format(new Date(data.endDate), "MMM dd, yyyy")}
                  </span>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {getSelectedResources(data.resourceIds).map((resource) => (
                  <span
                    key={resource.id}
                    className="inline-flex h-9 items-center rounded-full border border-border bg-muted px-3 text-sm font-normal text-foreground"
                  >
                    {resource.name}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {data.fileId && fileUrl ? (
            <SpotIncentiveImagePreview
              images={[{ src: fileUrl, alt: data.uploadedFileName || "Evidence image" }]}
              allowZoom
            >
              <div className="space-y-2">
                <p className={FIELD_LABEL}>
                  <span className="inline-flex items-center gap-1.5">
                    <FileText className="size-3.5" />
                    Evidence
                  </span>
                </p>
                <div className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-background p-2.5 hover:bg-muted/30">
                  <ImageThumbnail
                    src={fileThumbnailUrl || fileUrl}
                    alt={data.uploadedFileName || "Evidence image"}
                    index={0}
                    className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-border bg-muted"
                  />
                  <p className="min-w-0 flex-1 truncate text-sm">
                    {data.uploadedFileName || "Evidence image"}
                  </p>
                </div>
              </div>
            </SpotIncentiveImagePreview>
          ) : null}
        </div>

        <div className="flex shrink-0 justify-end px-5 pb-6 pt-4 sm:px-7">
          <Button
            type="button"
            onClick={handleClose}
            className="h-9 rounded-full px-3 text-sm font-normal"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SpotIncentivePreview;
